import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, ShieldCheck, UserPlus } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import UpgradeBadge from "@/components/UpgradeBadge";
import UpgradeNudge from "@/components/UpgradeNudge";

const ROLE_DESC = {
    Owner: "Full access. One per organization (the founder).",
    Admin: "Manage everything except ownership transfer.",
    Accountant: "Full accounting access; cannot manage billing.",
    Manager: "Run invoices, bills, customers, vendors.",
    Staff: "Create estimates, expenses, and customers.",
    Auditor: "Read-only access to reports and data.",
};

export default function Team() {
    const { currentOrg, features } = useAuth();
    const [data, setData] = useState({ members: [], roles: [] });
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ email: "", role: "Staff" });

    const load = async () => {
        if (!currentOrg) return;
        try {
            const { data: d } = await api.get(`/orgs/${currentOrg.id}/members`);
            setData(d);
        } catch (e) { /* ignore */ }
    };
    useEffect(() => { load(); }, [currentOrg]);

    const invite = async () => {
        try {
            await api.post(`/orgs/${currentOrg.id}/invite`, form);
            toast.success(`Invited ${form.email}`);
            setOpen(false); setForm({ email: "", role: "Staff" }); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const changeRole = async (id, role) => {
        try { await api.patch(`/orgs/${currentOrg.id}/members/${id}`, { role }); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const remove = async (id) => {
        if (!window.confirm("Suspend this member?")) return;
        try { await api.delete(`/orgs/${currentOrg.id}/members/${id}`); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const active = data.members.filter((m) => m.status === "active").length;

    return (
        <div>
            <PageHeader
                subtitle="Workspace"
                title="Team & roles"
                action={
                    <div className="flex items-center gap-3">
                        <Badge className="bg-ocean-50 text-ocean-700 border-0">{active} / {features.max_users ?? "-"} seats used</Badge>
                        {active >= (features.max_users ?? 0) && <UpgradeBadge message="Add more seats" />}
                        <Dialog open={open} onOpenChange={setOpen}>
                            <DialogTrigger asChild><Button className="bg-ocean-700 text-white rounded-xl" data-testid="invite-member-btn"><UserPlus className="h-4 w-4 mr-1.5" /> Invite</Button></DialogTrigger>
                            <DialogContent>
                                <DialogHeader><DialogTitle>Invite teammate</DialogTitle></DialogHeader>
                                <div className="space-y-4">
                                    <div><Label>Email</Label><Input className="mt-1.5" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="invite-email-input" /></div>
                                    <div>
                                        <Label>Role</Label>
                                        <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                                            <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                                            <SelectContent>{data.roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                                        </Select>
                                        <p className="text-xs text-muted-foreground mt-2">{ROLE_DESC[form.role]}</p>
                                    </div>
                                </div>
                                <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={invite} className="bg-ocean-700 text-white" data-testid="invite-submit-btn">Send invite</Button></DialogFooter>
                            </DialogContent>
                        </Dialog>
                    </div>
                }
            />
            <UpgradeNudge resource="users" />
            <div className="grid lg:grid-cols-[1fr,340px] gap-6">
                <div className="rounded-3xl bg-white border border-sand-200 shadow-card overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-sand-100 text-xs uppercase text-muted-foreground">
                            <tr><th className="text-left px-5 py-3">Member</th><th className="text-left px-5 py-3">Email</th><th className="text-left px-5 py-3">Role</th><th className="text-left px-5 py-3">Status</th><th className="text-left px-5 py-3">Joined</th><th></th></tr>
                        </thead>
                        <tbody className="divide-y divide-sand-200">
                            {data.members.map((m) => (
                                <tr key={m.id} className="hover:bg-sand-50/50">
                                    <td className="px-5 py-3 font-medium text-ocean-700">{m.name || "—"}</td>
                                    <td className="px-5 py-3 text-muted-foreground">{m.email}</td>
                                    <td className="px-5 py-3">
                                        <Select value={m.role} onValueChange={(v) => changeRole(m.id, v)}>
                                            <SelectTrigger className="w-36 h-9"><SelectValue /></SelectTrigger>
                                            <SelectContent>{data.roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </td>
                                    <td className="px-5 py-3"><Badge className={`border-0 ${m.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-sand-200 text-ocean-700"}`}>{m.status}</Badge></td>
                                    <td className="px-5 py-3 text-muted-foreground">{fmtDate(m.created_at)}</td>
                                    <td className="px-5 py-3 text-right">{m.role !== "Owner" && <Button variant="ghost" size="sm" onClick={() => remove(m.id)}>Remove</Button>}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="rounded-3xl bg-white border border-sand-200 shadow-card p-6">
                    <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-secondary" /><h3 className="font-heading text-lg text-ocean-700">Roles & permissions</h3></div>
                    <div className="mt-4 space-y-3">
                        {(data.roles || []).map((r) => (
                            <div key={r} className="rounded-xl border border-sand-200 p-3">
                                <div className="font-medium text-ocean-700">{r}</div>
                                <div className="text-xs text-muted-foreground mt-1">{ROLE_DESC[r]}</div>
                            </div>
                        ))}
                    </div>
                    {!features.custom_roles && (
                        <div className="mt-4 rounded-xl bg-coral/5 border border-coral/20 p-4 text-xs text-ocean-700">
                            Custom roles are available on the <span className="font-medium">Team</span> plan.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
